import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@devhop/ui';
import { HelpCircle } from 'lucide-react';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import {
    usePromotionEmployeesLookupQuery,
    usePromotionHistoryQuery,
    usePromotionPositionsLookupQuery,
} from '../api/queries';
import {
    PromotionRecordsFilter,
    type PromotionRecordsSearchParams,
} from '../ui/PromotionRecordsFilter';
import { PromotionTimeline } from '../ui/PromotionTimeline';
import {
    PromotionTour,
    promotionHistoryTourSteps,
    usePromotionTour,
} from '../tour';

export function PromotionHistoryPage() {
    const nav = useNavigate({ from: '/app/hr/promotion/history' });
    const search = useSearch({
        from: '/app/hr/promotion/history',
    }) as PromotionRecordsSearchParams;

    const query = {
        offset: search.offset ?? 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        employeeId: search.employeeId,
        periodYear: search.periodYear,
        periodMonth: search.periodMonth,
        positionId: search.positionId,
        search: search.search,
    };

    const historyQuery = usePromotionHistoryQuery(query);
    const employeesQuery = usePromotionEmployeesLookupQuery();
    const positionsQuery = usePromotionPositionsLookupQuery();
    const tour = usePromotionTour('history');

    return (
        <>
            <Header />
            <Main>
                <PromotionTour
                    steps={promotionHistoryTourSteps}
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center gap-2"
                    data-tourid="promotion-history-header"
                >
                    <h1 className="font-semibold text-2xl">
                        ປະຫວັດການເລື່ອນຕຳແໜ່ງ
                    </h1>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 rounded-full"
                        onClick={tour.startTour}
                        title="ເລີ່ມທົດສອບການນຳທາງ"
                        aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                    >
                        <HelpCircle className="size-5" />
                    </Button>
                </div>

                <div
                    className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
                    data-tourid="promotion-history-filter"
                >
                    <PromotionRecordsFilter
                        fromRoute="/app/hr/promotion/history"
                        searchParams={search}
                        employeeOptions={(employeesQuery.data ?? []).map(
                            (item) => ({ value: item.id, label: item.label }),
                        )}
                        positionOptions={(positionsQuery.data ?? []).map(
                            (item) => ({ value: item.id, label: item.label }),
                        )}
                    />
                    <div data-tourid="promotion-history-timeline">
                        <PromotionTimeline
                            items={historyQuery.data?.data ?? []}
                            isLoading={historyQuery.isLoading}
                            sortBy={search.sort?.[0]?.field ?? 'promotedAt'}
                            sortOrder={
                                search.sort?.[0]?.dir === 'asc' ? 'asc' : 'desc'
                            }
                            onViewDetail={(row) =>
                                nav({
                                    to: '/app/hr/promotion/records/$id',
                                    params: { id: row.id },
                                })
                            }
                            onSortingChange={(id, desc) => {
                                nav({
                                    search: {
                                        offset: 0,
                                        limit: search.limit ?? 20,
                                        sort: [
                                            {
                                                field: id,
                                                dir: desc ? 'desc' : 'asc',
                                            },
                                        ],
                                        employeeId: search.employeeId,
                                        periodYear: search.periodYear,
                                        periodMonth: search.periodMonth,
                                        positionId: search.positionId,
                                        search: search.search,
                                    },
                                });
                            }}
                        />
                    </div>
                </div>

                <div className="mt-6 flex justify-end">
                    <Button
                        type="button"
                        variant="link"
                        className="text-muted-foreground text-xs hover:text-foreground"
                        onClick={() => {
                            const limit = search.limit ?? 20;
                            nav({
                                search: {
                                    offset: (search.offset ?? 0) + limit,
                                    limit,
                                    sort: search.sort,
                                    employeeId: search.employeeId,
                                    periodYear: search.periodYear,
                                    periodMonth: search.periodMonth,
                                    positionId: search.positionId,
                                    search: search.search,
                                },
                            });
                        }}
                    >
                        ໂຫຼດເພີ່ມ
                    </Button>
                </div>
            </Main>
        </>
    );
}
